import { useCallback, useRef } from 'react';
import { useOpenWorkspacePanel } from '../panels/use-open-workspace-panel';
import { WorkspacePanel } from '../panels/workspace-panel';
import { SENSOR_EVENT_PANEL, SensorEventWorkspacePanel } from './client-only-panel-types';

export type OpenSensorEventPanelArgs = {
    alertKey: string | undefined;
};

export type OpenSensorEventPanel = (args: OpenSensorEventPanelArgs) => void;

/**
 * Opens the panel for one sensor event, or focuses it if that event is already open.
 *
 * `matchExistingPanel` is per-alert for the same reason the camera opener's is per-sensor:
 * `SENSOR_EVENT` behaves as an `ENTITY` panel, so the default matcher would match on panel type
 * alone and clicking a second event would focus the first event's panel.
 */
export function useOpenSensorEventPanel(): OpenSensorEventPanel {
    // Stable identity, same reasoning as `useOpenSensorPanel`: this gets passed down into rows and
    // list items that would otherwise re-render on every parent render.
    const openWorkspacePanel = useOpenWorkspacePanel();
    const openWorkspacePanelRef = useRef(openWorkspacePanel);
    openWorkspacePanelRef.current = openWorkspacePanel;

    return useCallback(({ alertKey }: OpenSensorEventPanelArgs) => {
        if (!alertKey) {
            return;
        }

        openWorkspacePanelRef.current({
            workspacePanelConfig: {
                workspacePanelType: SENSOR_EVENT_PANEL,
                // `panelState` is typed off `workspacePanelType`, which is the whole enum union
                // here because the literal has no enum member to narrow to. The cast is contained
                // to this one call; everything reading it goes through `SensorEventPanel`.
                panelState: { alertKey } as never,
            },
            matchExistingPanel: (workspacePanel: WorkspacePanel) => {
                if (workspacePanel.workspacePanelType !== SENSOR_EVENT_PANEL) {
                    return false;
                }
                return (workspacePanel as SensorEventWorkspacePanel).getPanelState('alertKey') === alertKey;
            },
        });
    }, []);
}
